/**
 * ActionPage — handles one-time tokenised email action links.
 *
 * Route: /action/:tokenId
 *
 * Works WITHOUT requiring the player to be logged in.
 * The token ID is the auth credential — validated server-side inside
 * playerReallocateChoice, which marks it as used atomically.
 *
 * No auto-redirect on success. Player stays on this page and can
 * close the tab — the confirmation email contains all the details.
 */

import React, { useEffect, useState }    from 'react';
import { useParams, Link }               from 'react-router-dom';
import { doc, getDoc }                   from 'firebase/firestore';
import { getFunctions, httpsCallable }   from 'firebase/functions';
import { db }                            from '../services/firebase';

// ─── Styles ────────────────────────────────────────────────────────────────────

const PAGE_STYLE = {
  minHeight:    '100dvh',
  background:   '#07111F',
  padding:      '0 16px',
  fontFamily:   'inherit',
};

const CARD_STYLE = {
  backgroundColor: '#0D1628',
  border:          '1px solid rgba(255,255,255,0.1)',
  borderRadius:    '12px',
  padding:         '40px',
  maxWidth:        '440px',
  margin:          '80px auto',
  textAlign:       'center',
  color:           '#F8F9FA',
};

const LOGO_STYLE = {
  fontWeight:      800,
  fontSize:        '18px',
  letterSpacing:   '0.05em',
  textTransform:   'uppercase',
  color:           '#F8F9FA',
  marginBottom:    '32px',
  display:         'block',
};

const BTN = (bg, fg = '#fff') => ({
  display:         'inline-block',
  marginTop:       '20px',
  padding:         '12px 28px',
  borderRadius:    '8px',
  background:      bg,
  color:           fg,
  fontWeight:      700,
  fontSize:        '14px',
  textDecoration:  'none',
  border:          'none',
  cursor:          'pointer',
});

// ─── Spinner ────────────────────────────────────────────────────────────────────

const Spinner = () => (
  <>
    <div style={{
      width: '40px', height: '40px',
      margin: '0 auto 20px',
      borderRadius: '50%',
      border: '3px solid rgba(255,255,255,0.1)',
      borderTopColor: '#1A4FFF',
      animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </>
);

// ─── Action label map ──────────────────────────────────────────────────────────

const ACTION_LABELS = {
  redistribute: 'Redistribute Across My Runners',
  expensive:    'Buy Most Expensive Runner',
  random:       'Pick a Runner Randomly',
  manual:       'Choose Manually',
};

// ─── Component ─────────────────────────────────────────────────────────────────

function ActionPage() {
  const { tokenId } = useParams();

  // phase: 'loading' | 'processing' | 'done' | 'used' | 'expired' | 'error'
  const [phase,       setPhase]       = useState('loading');
  const [errorMsg,    setErrorMsg]    = useState('');
  const [note,        setNote]        = useState('');
  const [actionLabel, setActionLabel] = useState('');

  useEffect(() => {
    if (!tokenId) {
      setErrorMsg('No action token found in this link.');
      setPhase('error');
      return;
    }

    let cancelled = false;

    async function run() {
      // 1. Read token doc (public read rule — no auth required)
      let tokenData;
      try {
        const snap = await getDoc(doc(db, 'action_tokens', tokenId));
        if (!snap.exists()) {
          setPhase('error');
          setErrorMsg('Link not found or already used.');
          return;
        }
        tokenData = snap.data();
      } catch (err) {
        setPhase('error');
        setErrorMsg('Could not read this link. Please try again.');
        return;
      }

      if (cancelled) return;

      // 2. Already used
      if (tokenData.used) {
        setPhase('used');
        return;
      }

      // 3. Expired
      const expiresMs =
        (typeof tokenData.expiresAt?.toMillis === 'function'
          ? tokenData.expiresAt.toMillis()
          : null) ||
        (tokenData.expiresAt?._seconds
          ? tokenData.expiresAt._seconds * 1000
          : null) ||
        (typeof tokenData.expiresAt === 'number'
          ? tokenData.expiresAt
          : null);

      if (expiresMs && Date.now() > expiresMs) {
        setPhase('expired');
        return;
      }

      // 4. Valid — call the Cloud Function
      setActionLabel(ACTION_LABELS[tokenData.action] || tokenData.action);
      setPhase('processing');

      try {
        const fns  = getFunctions(undefined, 'australia-southeast1');
        const fn   = httpsCallable(fns, 'playerReallocateChoice');
        const res  = await fn({
          slateId: tokenData.slateId,
          raceId:  tokenData.raceId,
          action:  tokenData.action,
          tokenId,
        });

        if (cancelled) return;
        setNote(res.data?.allocationNote || 'Your refund has been allocated.');
        setPhase('done');
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err?.message || 'Something went wrong. Please log in to manage your allocation.');
        setPhase('error');
      }
    }

    run();
    return () => { cancelled = true; };
  }, [tokenId]);

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={PAGE_STYLE}>
      <div style={CARD_STYLE}>

        {/* Logo */}
        <span style={LOGO_STYLE}>
          Syndicator<span style={{ color: '#1A4FFF' }}>Showdown</span>
        </span>

        {/* ── Loading ──────────────────────────────────────────────────────── */}
        {phase === 'loading' && (
          <>
            <Spinner />
            <p style={{ color: '#94A3B8', fontSize: '15px', margin: 0 }}>
              Validating your action link…
            </p>
          </>
        )}

        {/* ── Processing ───────────────────────────────────────────────────── */}
        {phase === 'processing' && (
          <>
            <Spinner />
            <p style={{ color: '#F8F9FA', fontSize: '16px', fontWeight: 700, margin: '0 0 8px' }}>
              Processing your selection…
            </p>
            {actionLabel && (
              <p style={{ color: '#94A3B8', fontSize: '14px', margin: 0 }}>
                {actionLabel}
              </p>
            )}
          </>
        )}

        {/* ── Done ─────────────────────────────────────────────────────────── */}
        {phase === 'done' && (
          <>
            <div style={{
              background:    'rgba(34,197,94,0.1)',
              border:        '1px solid #22c55e',
              borderRadius:  '10px',
              padding:       '24px 20px',
              marginBottom:  '8px',
            }}>
              <div style={{ fontSize: '36px', marginBottom: '12px' }}>✓</div>
              <h2 style={{ color: '#4ade80', fontWeight: 700, fontSize: '20px', margin: '0 0 12px' }}>
                Done!
              </h2>
              <p style={{ color: '#bbf7d0', fontSize: '14px', margin: 0, lineHeight: 1.6 }}>
                {note}
              </p>
            </div>

            <p style={{ color: '#64748B', fontSize: '13px', margin: '16px 0 0', lineHeight: 1.6 }}>
              A confirmation email has been sent to you with your new selection details.
            </p>

            <Link to="/my-stable" style={BTN('#1A4FFF')}>
              View My Stable →
            </Link>
          </>
        )}

        {/* ── Already used ─────────────────────────────────────────────────── */}
        {phase === 'used' && (
          <div style={{
            background:   'rgba(251,191,36,0.08)',
            border:       '1px solid #F6B42A',
            borderRadius: '10px',
            padding:      '24px 20px',
          }}>
            <div style={{ fontSize: '28px', marginBottom: '12px' }}>✓</div>
            <p style={{ color: '#F6B42A', fontWeight: 700, fontSize: '15px', margin: '0 0 10px' }}>
              This action has already been completed.
            </p>
            <p style={{ color: '#94A3B8', fontSize: '13px', margin: 0, lineHeight: 1.6 }}>
              Check your email for a confirmation of your allocation.
            </p>
          </div>
        )}

        {/* ── Expired ──────────────────────────────────────────────────────── */}
        {phase === 'expired' && (
          <>
            <div style={{
              background:   'rgba(100,116,139,0.12)',
              border:       '1px solid #475569',
              borderRadius: '10px',
              padding:      '24px 20px',
              marginBottom: '8px',
            }}>
              <div style={{ fontSize: '28px', marginBottom: '12px' }}>⏱</div>
              <p style={{ color: '#CBD5E1', fontWeight: 700, fontSize: '15px', margin: '0 0 10px' }}>
                This link has expired.
              </p>
              <p style={{ color: '#94A3B8', fontSize: '13px', margin: 0, lineHeight: 1.6 }}>
                Please log in to manage your stable.
              </p>
            </div>
            <Link to="/signin" style={BTN('#334155')}>
              Log In →
            </Link>
          </>
        )}

        {/* ── Error ────────────────────────────────────────────────────────── */}
        {phase === 'error' && (
          <>
            <div style={{
              background:   'rgba(239,68,68,0.08)',
              border:       '1px solid #ef4444',
              borderRadius: '10px',
              padding:      '24px 20px',
              marginBottom: '8px',
            }}>
              <div style={{ fontSize: '28px', marginBottom: '12px' }}>⚠️</div>
              <p style={{ color: '#f87171', fontWeight: 700, fontSize: '15px', margin: '0 0 10px' }}>
                Action could not be completed
              </p>
              <p style={{ color: '#fca5a5', fontSize: '13px', margin: 0, lineHeight: 1.6 }}>
                {errorMsg}
              </p>
            </div>
            <Link to="/signin" style={BTN('#334155')}>
              Log In to Manage Manually →
            </Link>
          </>
        )}

      </div>
    </div>
  );
}

export default ActionPage;
